import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Shield, ArrowLeft, Loader2, LogOut, Users, Plus, Trash2,
  Pencil, X, Save, Search, Ban, CheckCircle, Eye, EyeOff,
  RefreshCw, Copy, Check, UserPlus, Download, Filter,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api/admin`;

const STATUS_MAP = {
  trial: { label: "Trial", color: "bg-amber-100 text-amber-700 border-amber-200" },
  ativo: { label: "Ativo", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  expirado: { label: "Expirado", color: "bg-rose-100 text-rose-700 border-rose-200" },
  bloqueado: { label: "Bloqueado", color: "bg-gray-200 text-gray-600 border-gray-300" },
  aguardando_aprovacao: { label: "Aguardando", color: "bg-blue-100 text-blue-700 border-blue-200" },
};

function generatePassword() {
  const chars = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%";
  let pwd = "";
  for (let i = 0; i < 10; i++) pwd += chars[Math.floor(Math.random() * chars.length)];
  return pwd;
}

export const AdminUsersPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading, logout, getAuthHeaders } = useAdminAuth();

  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Create user dialog
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", email: "", password: "", plan_type: "trial", autoPassword: true });
  const [creating, setCreating] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);

  // Batch create dialog
  const [showBatch, setShowBatch] = useState(false);
  const [batchText, setBatchText] = useState("");
  const [batchPlan, setBatchPlan] = useState("trial");
  const [batchCreating, setBatchCreating] = useState(false);
  const [batchResults, setBatchResults] = useState(null);

  // Edit dialog
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", plan_type: "" });
  const [saving, setSaving] = useState(false);

  // Delete confirm
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Copied state
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (!loading && !isAuthenticated) navigate("/admin/login");
  }, [loading, isAuthenticated, navigate]);

  const fetchUsers = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoadingUsers(true);
    try {
      const res = await axios.get(`${API}/users`, { headers: getAuthHeaders() });
      setUsers(res.data.users || []);
    } catch { toast.error("Erro ao carregar usuários"); }
    finally { setLoadingUsers(false); }
  }, [isAuthenticated, getAuthHeaders]);

  const fetchPlans = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await axios.get(`${API}/plans`, { headers: getAuthHeaders() });
      setPlans(res.data.plans || []);
    } catch { /* ignore */ }
  }, [isAuthenticated, getAuthHeaders]);

  useEffect(() => { fetchUsers(); fetchPlans(); }, [fetchUsers, fetchPlans]);

  const handleCreateUser = async () => {
    if (!createForm.name.trim() || !createForm.email.trim()) {
      toast.error("Preencha nome e email"); return;
    }
    setCreating(true);
    try {
      const payload = {
        name: createForm.name.trim(),
        email: createForm.email.trim(),
        password: createForm.autoPassword ? null : createForm.password,
        plan_type: createForm.plan_type,
      };
      const res = await axios.post(`${API}/users`, payload, { headers: getAuthHeaders() });
      setCreatedUser(res.data);
      toast.success("Usuario criado com sucesso!");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar usuário");
    } finally { setCreating(false); }
  };

  const handleBatchCreate = async () => {
    const lines = batchText.trim().split("\n").filter(l => l.trim());
    if (lines.length === 0) { toast.error("Adicione pelo menos um usuário"); return; }
    
    const usersToCreate = [];
    for (const line of lines) {
      const parts = line.split(",").map(p => p.trim());
      if (parts.length < 2) { toast.error(`Linha invalida: "${line}". Use: nome, email`); return; }
      usersToCreate.push({ name: parts[0], email: parts[1], password: parts[2] || null, plan_type: batchPlan });
    }
    
    setBatchCreating(true);
    try {
      const res = await axios.post(`${API}/users/batch`, { users: usersToCreate }, { headers: getAuthHeaders() });
      setBatchResults(res.data);
      toast.success(`${res.data.created} de ${res.data.total} usuário(s) criado(s)`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao criar usuários em lote");
    } finally { setBatchCreating(false); }
  };

  const handleEditUser = async () => {
    if (!editForm.name.trim()) { toast.error("Nome obrigatório"); return; }
    setSaving(true);
    try {
      const payload = {};
      if (editForm.name !== editUser.name) payload.name = editForm.name.trim();
      if (editForm.email !== editUser.email) payload.email = editForm.email.trim();
      if (editForm.plan_type) payload.plan_type = editForm.plan_type;
      
      await axios.put(`${API}/users/${editUser.id}`, payload, { headers: getAuthHeaders() });
      toast.success("Usuario atualizado!");
      setEditUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao atualizar");
    } finally { setSaving(false); }
  };

  const handleToggleBlock = async (user) => {
    try {
      const res = await axios.put(`${API}/users/${user.id}/toggle-block`, {}, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      fetchUsers();
    } catch { toast.error("Erro ao bloquear/desbloquear"); }
  };

  const handleDeleteUser = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/users/${deleteId}`, { headers: getAuthHeaders() });
      toast.success("Usuario excluido!");
      setDeleteId(null);
      fetchUsers();
    } catch { toast.error("Erro ao excluir"); }
    finally { setDeleting(false); }
  };

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filteredUsers = users.filter(u => {
    if (statusFilter !== "all" && (u.calculated_status || u.status) !== statusFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
    }
    return true;
  });

  if (loading || !isAuthenticated) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-rose-500" /></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-users-page">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard">
              <Button variant="ghost" size="icon" className="text-gray-500 hover:text-gray-900" data-testid="back-to-dashboard">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-bold text-gray-900">Gerenciar Usuarios</h1>
              <p className="text-xs text-gray-500">{users.length} usuário(s) cadastrado(s)</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" className="gap-2 bg-indigo-600 hover:bg-indigo-700" onClick={() => { setCreateForm({ name: "", email: "", password: "", plan_type: "trial", autoPassword: true }); setCreatedUser(null); setShowCreate(true); }} data-testid="create-user-button">
              <Plus className="w-4 h-4" /> Criar Usuario
            </Button>
            <Button size="sm" variant="outline" className="gap-2" onClick={() => { setBatchText(""); setBatchResults(null); setShowBatch(true); }} data-testid="batch-create-button">
              <UserPlus className="w-4 h-4" /> Criar em Lote
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/admin/login"); }} className="gap-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid="admin-logout-button">
              <LogOut className="w-4 h-4" /> Sair
            </Button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Buscar por nome ou email..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-white" data-testid="search-users-input" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px] bg-white" data-testid="status-filter-select">
              <Filter className="w-4 h-4 mr-2 text-gray-400" />
              <SelectValue placeholder="Filtrar status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="trial">Trial</SelectItem>
              <SelectItem value="ativo">Ativo</SelectItem>
              <SelectItem value="expirado">Expirado</SelectItem>
              <SelectItem value="bloqueado">Bloqueado</SelectItem>
              <SelectItem value="aguardando_aprovacao">Aguardando</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="icon" onClick={fetchUsers} className="shrink-0" data-testid="refresh-users-button">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Users Table */}
        {loadingUsers ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-20">
            <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">{search || statusFilter !== "all" ? "Nenhum usuário encontrado com esses filtros" : "Nenhum usuário cadastrado"}</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="users-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Usuario</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Plano</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Criado em</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredUsers.map((user) => {
                    const st = STATUS_MAP[user.calculated_status || user.status] || STATUS_MAP.trial;
                    return (
                      <tr key={user.id} className="hover:bg-gray-50/50 transition-colors" data-testid={`user-row-${user.id}`}>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{user.name}</p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${st.color}`}>{st.label}</span>
                          {user.days_remaining > 0 && <span className="text-[10px] text-gray-400 ml-1.5">{user.days_remaining}d</span>}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-600">{user.plan_name || "Trial"}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-500">
                            {new Date(user.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-500 hover:text-indigo-600" onClick={() => { setEditUser(user); setEditForm({ name: user.name, email: user.email, plan_type: "" }); }} data-testid={`edit-user-${user.id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${user.status === "bloqueado" ? "text-emerald-600 hover:text-emerald-700" : "text-amber-600 hover:text-amber-700"}`} onClick={() => handleToggleBlock(user)} data-testid={`block-user-${user.id}`}>
                              {user.status === "bloqueado" ? <CheckCircle className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                            </Button>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-rose-500 hover:text-rose-700" onClick={() => setDeleteId(user.id)} data-testid={`delete-user-${user.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Create User Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-gray-900">{createdUser ? "Usuario Criado!" : "Criar Novo Usuario"}</DialogTitle>
            <DialogDescription>{createdUser ? "Copie as credenciais abaixo:" : "Preencha os dados do novo usuário"}</DialogDescription>
          </DialogHeader>

          {createdUser ? (
            <div className="space-y-3 py-2">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Nome:</span>
                  <span className="font-medium text-gray-900">{createdUser.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Email:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{createdUser.email}</span>
                    <button onClick={() => handleCopy(createdUser.email, "email")} className="text-gray-400 hover:text-gray-600">
                      {copiedId === "email" ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Senha:</span>
                  <div className="flex items-center gap-2">
                    <code className="bg-white px-2 py-0.5 rounded text-sm font-mono border">{createdUser.generated_password}</code>
                    <button onClick={() => handleCopy(createdUser.generated_password, "pwd")} className="text-gray-400 hover:text-gray-600">
                      {copiedId === "pwd" ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
              </div>
              <Button className="w-full" variant="outline" onClick={() => handleCopy(`Email: ${createdUser.email}\nSenha: ${createdUser.generated_password}`, "all")} data-testid="copy-all-credentials">
                {copiedId === "all" ? <><Check className="w-4 h-4 mr-2" /> Copiado!</> : <><Copy className="w-4 h-4 mr-2" /> Copiar Tudo</>}
              </Button>
              <DialogFooter>
                <Button onClick={() => { setCreatedUser(null); setShowCreate(false); }} data-testid="close-created-dialog">Fechar</Button>
                <Button variant="outline" onClick={() => { setCreatedUser(null); setCreateForm({ name: "", email: "", password: "", plan_type: "trial", autoPassword: true }); }} data-testid="create-another-button">Criar Outro</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label className="text-gray-700">Nome</Label>
                <Input placeholder="Nome completo" value={createForm.name} onChange={(e) => setCreateForm(f => ({ ...f, name: e.target.value }))} data-testid="create-user-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-gray-700">Email</Label>
                <Input type="email" placeholder="email@exemplo.com" value={createForm.email} onChange={(e) => setCreateForm(f => ({ ...f, email: e.target.value }))} data-testid="create-user-email" />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-gray-700">Senha</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Gerar automatica</span>
                    <Switch checked={createForm.autoPassword} onCheckedChange={(v) => setCreateForm(f => ({ ...f, autoPassword: v, password: v ? "" : generatePassword() }))} data-testid="auto-password-switch" />
                  </div>
                </div>
                {!createForm.autoPassword && (
                  <Input type="text" placeholder="Min. 6 caracteres" value={createForm.password} onChange={(e) => setCreateForm(f => ({ ...f, password: e.target.value }))} data-testid="create-user-password" />
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-gray-700">Plano</Label>
                <Select value={createForm.plan_type} onValueChange={(v) => setCreateForm(f => ({ ...f, plan_type: v }))}>
                  <SelectTrigger data-testid="create-user-plan">
                    <SelectValue placeholder="Selecionar plano" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial (7 dias)</SelectItem>
                    <SelectItem value="ativo_ilimitado">Gratuito Ilimitado</SelectItem>
                    {plans.filter(p => p.active).map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name} ({p.duration_days} dias)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
                <Button onClick={handleCreateUser} disabled={creating} className="bg-indigo-600 hover:bg-indigo-700" data-testid="confirm-create-user">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-2" />Criar</>}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Batch Create Dialog */}
      <Dialog open={showBatch} onOpenChange={setShowBatch}>
        <DialogContent className="sm:max-w-lg" data-testid="batch-create-dialog">
          <DialogHeader>
            <DialogTitle className="text-gray-900">{batchResults ? "Resultado da Criação em Lote" : "Criar Usuários em Lote"}</DialogTitle>
            <DialogDescription>{batchResults ? `${batchResults.created} de ${batchResults.total} criados` : "Uma linha por usuário: nome, email, senha (opcional)"}</DialogDescription>
          </DialogHeader>

          {batchResults ? (
            <div className="space-y-3 py-2 max-h-[400px] overflow-y-auto">
              {batchResults.results.map((r, i) => (
                <div key={i} className={`rounded-lg p-3 text-sm border ${r.success ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{r.name || r.email}</span>
                    {r.success ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <X className="w-4 h-4 text-rose-600" />}
                  </div>
                  {r.success && (
                    <div className="mt-1 flex items-center gap-2 text-xs text-gray-600">
                      <span>{r.email}</span>
                      <code className="bg-white px-1.5 py-0.5 rounded border font-mono">{r.password}</code>
                      <button onClick={() => handleCopy(`${r.email} / ${r.password}`, `batch-${i}`)} className="text-gray-400 hover:text-gray-600">
                        {copiedId === `batch-${i}` ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                      </button>
                    </div>
                  )}
                  {!r.success && <p className="text-xs text-rose-600 mt-1">{r.error}</p>}
                </div>
              ))}
              <DialogFooter>
                <Button onClick={() => { setBatchResults(null); setShowBatch(false); }}>Fechar</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label className="text-gray-700">Usuarios (um por linha)</Label>
                <textarea
                  className="w-full h-40 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none font-mono"
                  placeholder={"João Silva, joao@email.com\nMaria Santos, maria@email.com, senha123\nPedro Lima, pedro@email.com"}
                  value={batchText}
                  onChange={(e) => setBatchText(e.target.value)}
                  data-testid="batch-textarea"
                />
                <p className="text-xs text-gray-400">Formato: nome, email, senha (opcional - se omitida, sera gerada automaticamente)</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-gray-700">Plano para todos</Label>
                <Select value={batchPlan} onValueChange={setBatchPlan}>
                  <SelectTrigger data-testid="batch-plan-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial (7 dias)</SelectItem>
                    <SelectItem value="ativo_ilimitado">Gratuito Ilimitado</SelectItem>
                    {plans.filter(p => p.active).map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name} ({p.duration_days} dias)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowBatch(false)}>Cancelar</Button>
                <Button onClick={handleBatchCreate} disabled={batchCreating} className="bg-indigo-600 hover:bg-indigo-700" data-testid="confirm-batch-create">
                  {batchCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-2" />Criar em Lote</>}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={(open) => { if (!open) setEditUser(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Editar Usuario</DialogTitle>
            <DialogDescription>Altere os dados do usuário</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-gray-700">Nome</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))} data-testid="edit-user-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-gray-700">Email</Label>
              <Input type="email" value={editForm.email} onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))} data-testid="edit-user-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-gray-700">Alterar Plano</Label>
              <Select value={editForm.plan_type} onValueChange={(v) => setEditForm(f => ({ ...f, plan_type: v }))}>
                <SelectTrigger data-testid="edit-user-plan">
                  <SelectValue placeholder="Manter plano atual" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="keep">Manter atual</SelectItem>
                  <SelectItem value="trial">Trial (7 dias)</SelectItem>
                  <SelectItem value="ativo_ilimitado">Gratuito Ilimitado</SelectItem>
                  {plans.filter(p => p.active).map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name} ({p.duration_days} dias)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)}>Cancelar</Button>
            <Button onClick={handleEditUser} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="confirm-edit-user">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" />Salvar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteId} onOpenChange={(open) => { if (!open) setDeleteId(null); }}>
        <DialogContent className="sm:max-w-sm" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-600">Excluir Usuario</DialogTitle>
            <DialogDescription>Esta ação é irreversível. Todos os dados do usuário (ideias, compartilhamentos, pagamentos) serão excluídos.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDeleteUser} disabled={deleting} data-testid="confirm-delete-user">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4 mr-2" />Excluir</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersPage;
